import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
	Button,
	Card,
	Col,
	DatePicker,
	Form,
	Row,
	Space,
	Statistic,
	Table,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import * as XLSX from 'xlsx';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { handleAPIError } from '../../utils/helpers';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface VerificationRow {
	device_id: number;
	imei: string;
	customer_name: string;
	customer_phone: string;
	retailer_name: string;
	created_at: string;
	approved_at: string | null;
	approved_by: string | null;
	minutes_to_approve: number;
	status: 'approved' | 'pending';
}

interface VerificationSummary {
	total_approved: number;
	total_pending: number;
	avg_minutes: number;
	within_15_count: number;
	within_30_count: number;
	over_30_count: number;
}

interface VerificationResponse {
	devices: VerificationRow[];
	count: number;
	summary: VerificationSummary;
}

interface FilterValues {
	date_range: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const quickPresets: { label: string; value: [dayjs.Dayjs, dayjs.Dayjs] }[] = [
	{ label: 'Today', value: [dayjs().startOf('day'), dayjs()] },
	{ label: 'This Week', value: [dayjs().startOf('week'), dayjs()] },
	{ label: 'This Month', value: [dayjs().startOf('month'), dayjs()] },
];

const getSpeedColor = (minutes: number, status: string) => {
	if (status === 'pending') return 'blue';
	if (minutes <= 15) return 'green';
	if (minutes <= 30) return 'orange';
	return 'red';
};

const getSpeedLabel = (minutes: number, status: string) => {
	if (status === 'pending') return `Waiting ${minutes}m`;
	if (minutes <= 15) return 'Fast';
	if (minutes <= 30) return 'Acceptable';
	return 'Slow';
};

const getAvgColor = (avg: number) => {
	if (avg <= 15) return '#52c41a';
	if (avg <= 30) return '#fa8c16';
	return '#ff4d4f';
};

const VerificationTracker = () => {
	const [form] = Form.useForm<FilterValues>();
	const [filters, setFilters] = useState<FilterValues>({
		date_range: [dayjs().startOf('day'), dayjs()],
	});

	const { data, error, isLoading, refetch } = useQuery({
		queryKey: ['verification-tracker', filters],
		queryFn: async () => {
			const params = {
				start: filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : null,
				end: filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : null,
			};
			const response = await axios.get('/admin/reports/verification-tracker', {
				withCredentials: true,
				params,
			});
			return response.data.results as VerificationResponse;
		},
		refetchInterval: 60_000,
	});

	if (error) handleAPIError(error);

	const rows: VerificationRow[] = data?.devices || [];
	const summary = data?.summary;

	const handleExport = () => {
		if (!rows.length) return;
		const sheet = XLSX.utils.json_to_sheet(
			rows.map((r) => ({
				'Device ID': r.device_id,
				'IMEI': r.imei,
				'Customer': r.customer_name,
				'Phone': r.customer_phone,
				'Retailer': r.retailer_name,
				'Created At': dayjs(r.created_at).format('DD MMM YYYY HH:mm'),
				'Approved At': r.approved_at ? dayjs(r.approved_at).format('DD MMM YYYY HH:mm') : '',
				'Approved By': r.approved_by || '',
				'Minutes to Approve': r.minutes_to_approve,
				'Status': r.status === 'pending' ? 'Pending' : getSpeedLabel(r.minutes_to_approve, r.status),
			}))
		);
		sheet['!cols'] = [
			{ wch: 10 }, { wch: 22 }, { wch: 22 }, { wch: 16 }, { wch: 24 },
			{ wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 18 }, { wch: 14 },
		];
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, sheet, 'Verification');
		const start = filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : 'all';
		const end = filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : 'now';
		XLSX.writeFile(wb, `verification-tracker-${start} to ${end}.xlsx`);
	};

	const columns: ColumnsType<VerificationRow> = [
		{
			title: 'Device ID',
			dataIndex: 'device_id',
			key: 'device_id',
			width: 90,
			sorter: (a, b) => a.device_id - b.device_id,
		},
		{
			title: 'Customer',
			key: 'customer',
			width: 180,
			render: (_, r) => (
				<div>
					<div className="font-medium">{r.customer_name}</div>
					<div className="text-xs text-gray-500">{r.customer_phone}</div>
				</div>
			),
		},
		{
			title: 'Retailer',
			dataIndex: 'retailer_name',
			key: 'retailer_name',
			width: 200,
			sorter: (a, b) => a.retailer_name.localeCompare(b.retailer_name),
		},
		{
			title: 'Created At',
			dataIndex: 'created_at',
			key: 'created_at',
			width: 165,
			render: (v: string) => dayjs(v).format('DD MMM YYYY HH:mm'),
			sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
		},
		{
			title: 'Approved At',
			dataIndex: 'approved_at',
			key: 'approved_at',
			width: 165,
			render: (v: string | null) =>
				v ? (
					dayjs(v).format('DD MMM YYYY HH:mm')
				) : (
					<Text type="secondary">—</Text>
				),
		},
		{
			title: 'Approved By',
			dataIndex: 'approved_by',
			key: 'approved_by',
			width: 150,
			render: (v: string | null) =>
				v ? <Text>{v}</Text> : <Text type="secondary">—</Text>,
			sorter: (a, b) => (a.approved_by || '').localeCompare(b.approved_by || ''),
		},
		{
			title: 'Time Taken',
			dataIndex: 'minutes_to_approve',
			key: 'minutes_to_approve',
			width: 150,
			align: 'center',
			defaultSortOrder: 'descend',
			render: (v: number, r) => (
				<Tooltip title={r.status === 'pending' ? 'Still waiting for approval' : `${v} minutes from creation to approval`}>
					<Tag color={getSpeedColor(v, r.status)}>
						{r.status === 'pending' ? `Waiting ${v}m` : `${v} min — ${getSpeedLabel(v, r.status)}`}
					</Tag>
				</Tooltip>
			),
			sorter: (a, b) => a.minutes_to_approve - b.minutes_to_approve,
		},
	];

	return (
		<AppLayout
			title="Verification Tracker"
			subtitle="Device approval times — target: ≤15 min, max: 30 min"
		>
			{/* Summary Cards */}
			{summary && (
				<Card className="mb-6">
					<Row gutter={[16, 16]}>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Total Approved"
								value={summary.total_approved}
								valueStyle={{ color: '#1677ff' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Pending Now"
								value={summary.total_pending}
								valueStyle={{ color: summary.total_pending > 0 ? '#fa8c16' : '#52c41a' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Tooltip title="Average time from device creation to approval (approved devices only)">
								<Statistic
									title="Avg Verification Time"
									value={summary.avg_minutes}
									suffix="min"
									valueStyle={{ color: getAvgColor(summary.avg_minutes) }}
								/>
							</Tooltip>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Within 15 min"
								value={summary.within_15_count}
								valueStyle={{ color: '#52c41a' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="15–30 min"
								value={summary.within_30_count}
								valueStyle={{ color: '#fa8c16' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Over 30 min ⚠"
								value={summary.over_30_count}
								valueStyle={{ color: summary.over_30_count > 0 ? '#ff4d4f' : '#52c41a' }}
							/>
						</Col>
					</Row>
				</Card>
			)}

			{/* Filters */}
			<Card className="mb-4">
				<div className="flex flex-wrap items-center gap-3">
					<Form
						form={form}
						layout="inline"
						initialValues={{ date_range: [dayjs().startOf('day'), dayjs()] }}
					>
						<Form.Item name="date_range" label="Date Range">
							<RangePicker style={{ width: 280 }} placeholder={['Start Date', 'End Date']} />
						</Form.Item>
					</Form>
					<Space wrap>
						{quickPresets.map((p) => (
							<Button
								key={p.label}
								size="small"
								onClick={() => {
									form.setFieldsValue({ date_range: p.value });
									setFilters({ date_range: p.value });
								}}
							>
								{p.label}
							</Button>
						))}
					</Space>
					<Space>
						<Button type="primary" onClick={() => setFilters(form.getFieldsValue())}>
							Apply
						</Button>
						<Button
							onClick={() => {
								const def: [dayjs.Dayjs, dayjs.Dayjs] = [dayjs().startOf('day'), dayjs()];
								form.setFieldsValue({ date_range: def });
								setFilters({ date_range: def });
							}}
						>
							Reset
						</Button>
						<Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!rows.length}>
							Export
						</Button>
						<Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
							Refresh
						</Button>
					</Space>
					{data && (
						<Text type="secondary" className="ml-2 text-xs">
							Auto-refreshes every 60s
						</Text>
					)}
				</div>
			</Card>

			{/* Table */}
			<Card>
				<Table
					columns={columns}
					dataSource={rows}
					loading={isLoading}
					rowKey="device_id"
					scroll={{ x: 1200 }}
					rowClassName={(r) => (r.status === 'pending' ? 'bg-blue-50' : '')}
					pagination={{
						pageSize: 50,
						showSizeChanger: true,
						showTotal: (total) => `${total} devices`,
					}}
				/>
			</Card>
		</AppLayout>
	);
};

export default VerificationTracker;
